import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError, switchMap, take, tap } from "rxjs/operators";
import { Constants } from "../../../constants";
import { ArcusAuthService } from "../../core/arcusauth.service";

@Injectable()
export default class AuthInterceptorService implements HttpInterceptor {
    constructor(
        private authService: ArcusAuthService,
        private _router: Router
    ) {}

    // left as 'any' because interceptors are meant to be generic
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url.startsWith(Constants.apiEndpoint)) {
            return this.authService.isAnonymous$.pipe(
                take(1),
                switchMap((isAnonymous) => {
                    if (isAnonymous) {
                        // Skip adding token for anonymous users
                        return next.handle(req).pipe(
                            tap({
                                error: (error) => {
                                    const respError = error as HttpErrorResponse;
                                    if (respError && respError.status === 401) {
                                        this._router.navigateByUrl("/login");
                                    }
                                },
                            })
                        );
                    }

                    return this.authService.getAccessToken().pipe(
                        take(1),
                        catchError((err) => {
                            // Token retrieval failed due to expired session — the auth service handles
                            // the dialog and Sentry capture via auth.error$; silently cancel this request.
                            if (
                                err?.error === "login_required" ||
                                err?.message?.toLowerCase().includes("login required")
                            ) {
                                this.authService.handleSessionExpired();
                                return EMPTY;
                            }
                            if (
                                err?.error === "mfa_required" ||
                                err?.message?.toLowerCase().includes("multifactor authentication required")
                            ) {
                                this.authService.handleMfaRequired();
                                return EMPTY;
                            }
                            // Auth0 hidden-iframe token refresh timed out — treat as session expired
                            if (err?.message === "Timeout") {
                                this.authService.handleSessionExpired();
                                return EMPTY;
                            }
                            return throwError(() => err);
                        }),
                        switchMap((token) => {
                            let authReq = req;
                            if (token) {
                                const headers = new HttpHeaders().set("Authorization", `Bearer ${token}`);
                                authReq = req.clone({ headers });
                            }
                            return next.handle(authReq).pipe(
                                tap({
                                    error: (error) => {
                                        const respError = error as HttpErrorResponse;
                                        if (respError && (respError.status === 401 || respError.status === 403)) {
                                            if (this.authService.isAuthenticated) {
                                                this._router.navigateByUrl("/unauthorized");
                                            } else {
                                                this._router.navigateByUrl("/login");
                                            }
                                        }
                                    },
                                })
                            );
                        })
                    );
                })
            );
        } else {
            return next.handle(req);
        }
    }
}
